import { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { TextInput } from "@/components/we360/TextInput";
import { AlertBanner } from "@/components/we360/AlertBanner";
import VitalsMeasurement from "@/components/we360/VitalsMeasurement";
import { VitalsErrorBoundary } from "@/components/we360/VitalsErrorBoundary";
import { isAuthorizedDomain, isSDKSupported } from "@/services/fhvitals";
import { saveMeasurement, syncMetricsToGCP, FIELD_META } from "@/services/measurements";
import { syncF1 } from "@/services/backendSync";
import { PARQConsentGate } from "@/components/we360/PARQConsentGate";
import { resolveAlertsByType, getNormalAlertTypes } from "@/services/alertsService";
import { calculateAge } from "@/pages/FormF1/helpers";
import {
  ArrowLeft,
  Camera,
  Edit3,
  CheckCircle,
  Loader2,
  AlertCircle,
  UploadCloud,
} from "lucide-react";

/**
 * Estructura del formulario manual: cada "fila" puede ser un campo simple
 * o un par que se muestra lado a lado (caso presión arterial, que se mide
 * siempre en conjunto).
 */
const MANUAL_ROWS = [
  { type: "single", key: "peso" },
  { type: "single", key: "talla" },
  { type: "single", key: "cintura" },
  {
    type: "pair",
    heading: "Presión arterial",
    hint: "Sistólica y diastólica se miden siempre juntas",
    keys: ["paSistolica", "paDiastolica"],
  },
  { type: "single", key: "glucemia" },
  { type: "single", key: "oximetria" },
  { type: "single", key: "heartRate" },
];

/**
 * El UserProfile almacena los vitales como strings ("72.5") mientras que
 * saveMeasurement limpia a number (72.5). Convertimos de vuelta para que
 * el contexto y los inputs existentes no cambien su tipo de dato.
 */
function stringifyFields(numFields) {
  const out = {};
  for (const [k, v] of Object.entries(numFields || {})) {
    if (v === null || v === undefined) continue;
    out[k] = String(v);
  }
  return out;
}

export default function Measurement() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { profile, setProfile, isDemo, isReadOnly, isImpersonating } =
    useUserProfile();

  const searchParams = new URLSearchParams(location.search);
  const initialTab = searchParams.get("tab") || "selector";
  const [tab, setTab] = useState(initialTab); // 'selector' | 'manual' | 'camera'
  const [values, setValues] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showVitalsModal, setShowVitalsModal] = useState(false);
  const [parqConsentimiento, setParqConsentimiento] = useState(
    profile.parqConsentimiento || null
  );

  const parqAlerta = profile.f2Results?.parqAlerta;
  const parqRiskCount = profile.f2Results?.parqRiskCount || 0;
  const needsParqGate = parqAlerta && !parqConsentimiento;

  const domainOk = isAuthorizedDomain();
  const sdkCheck = isSDKSupported();
  const canUseCamera = domainOk && sdkCheck.supported;

  const userAge = useMemo(
    () => calculateAge(profile.fechaNacimiento),
    [profile.fechaNacimiento]
  );
  const userSex = profile.sexo === "Masculino" ? 1 : 0;

  const filledCount = Object.values(values).filter(
    (v) => v !== "" && v !== null && v !== undefined
  ).length;

  function setField(key, v) {
    setValues((prev) => ({ ...prev, [key]: v }));
  }

  async function handleSubmitManual() {
    if (filledCount === 0) {
      setError("Debes ingresar al menos un valor");
      return;
    }
    if (isReadOnly) {
      setError(
        isDemo
          ? "Modo demo: las mediciones no se guardan"
          : "Modo impersonación: solo lectura"
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const cleaned = await saveMeasurement(user.uid, {
        method: "manual",
        fields: values,
        note: note.trim() || null,
      });
      // Actualiza flat fields del perfil con timestamp de medición y estado Outbox
      setProfile((prev) => ({
        ...prev,
        ...stringifyFields(cleaned.fields),
        vitalsMeasurementDate: cleaned.timestamp,
        vitalsMeasurementMethod: "manual",
        fhirSyncStatus: "PENDING_FHIR",
      }));
      // Auto-resolver alertas de Firestore para métricas que ahora están en rango normal
      const normalTypes = getNormalAlertTypes(cleaned.fields);
      if (normalTypes.length > 0) resolveAlertsByType(user.uid, normalTypes).catch(() => {});
      // Sync mandatorio a GCP FHIR con seguimiento de estado Outbox (SYNCED / PENDING_FHIR)
      syncMetricsToGCP(user.email || profile?.email, cleaned.fields, {
        uid: user.uid,
        measurementId: cleaned.id,
      }).catch(() => {});
      navigate("/dashboard", {
        state: { measurementSaved: true, method: "manual" },
      });
    } catch (e) {
      setError(e.message || "Error al guardar");
      setSaving(false);
    }
  }

  async function handleCameraComplete(results) {
    setShowVitalsModal(false);
    if (isReadOnly) {
      setError(
        isDemo
          ? "Modo demo: las mediciones no se guardan"
          : "Modo impersonación: solo lectura"
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const fields = {
        paSistolica: results.bloodPressureSystolic,
        paDiastolica: results.bloodPressureDiastolic,
        oximetria: results.oxygenSaturation,
        heartRate: results.heartRate,
        heartRateVariability: results.heartRateVariability,
        respirationRate: results.respirationRate,
        stressIndex: results.stressIndex != null ? Math.round(results.stressIndex) : null,
        cardiovascularAge: results.cardiovascularAge != null ? Math.round(results.cardiovascularAge) : null,
        cardiovascularBMI: results.cardiovascularBMI,
        cvdRisk: results.cvdRisk,
        wellnessScore: results.wellnessScore,
        ansIndexPNS: results.ansIndexPNS,
        ansIndexSNS: results.ansIndexSNS,
        hba1c: results.hba1cValue ?? results.hba1c ?? null,
        hemoglobin: results.hemoglobin ?? null,
        cholesterol: results.cholesterolValue ?? results.cholesterol ?? null,
        shockIndex: results.shockIndex ?? null,
        prq: results.prq ?? null,
        hrvRMSSD: results.hrvRMSSD ?? null,
        hrvSDNN: results.hrvSDNN ?? null,
      };
      const cleaned = await saveMeasurement(user.uid, {
        method: "camera",
        fields,
        note: null,
      });
      const updatedProfile = {
        ...profile,
        uid: user.uid,
        email: user.email || profile?.email,
        ...stringifyFields(cleaned.fields),
        vitalsMeasurementDate: cleaned.timestamp,
        vitalsMeasurementMethod: "camera",
        fhirSyncStatus: "PENDING_FHIR",
        pendingFhirF1: true,
      };
      // Actualiza flat fields del perfil con timestamp de medición y estado Outbox
      await setProfile(updatedProfile);
      // Auto-resolver alertas de Firestore para métricas que ahora están en rango normal
      const normalTypes = getNormalAlertTypes(cleaned.fields);
      if (normalTypes.length > 0) resolveAlertsByType(user.uid, normalTypes).catch(() => {});
      const targetEmail = user.email || profile?.email;
      // 1. Sync de métricas LOINC rápidas (/api/patients/metrics) y marcado de medición en Outbox
      syncMetricsToGCP(targetEmail, cleaned.fields, {
        uid: user.uid,
        measurementId: cleaned.id,
      }).catch(() => {});
      // 2. Sync completo F1 (/api/gcp/forms) para persistir también los 7 biomarcadores compuestos we360-* y SDK v5.9.3 en FHIR Store
      if (targetEmail) {
        syncF1(updatedProfile, targetEmail).catch(() => {});
      }
      navigate("/dashboard", {
        state: { measurementSaved: true, method: "camera" },
      });
    } catch (e) {
      setError(e.message || "Error al guardar");
      setSaving(false);
    }
  }

  function openCamera() {
    if (!canUseCamera) return;
    if (!profile.peso || !profile.talla) {
      setError(
        "Necesitamos tu peso y talla para el escaneo facial. Actualízalos en Manual primero o en tu perfil."
      );
      return;
    }
    if (!profile.fechaNacimiento || !profile.sexo) {
      setError(
        "Completa tu fecha de nacimiento y sexo en el Formulario 1 antes de usar la cámara."
      );
      return;
    }
    setError(null);
    setShowVitalsModal(true);
  }

  const handleBack = () => {
    if (tab === "selector") {
      navigate("/dashboard");
    } else {
      setTab("selector");
    }
  };

  return (
    <div
      className="min-h-screen font-sans px-3 py-6 sm:px-6 relative overflow-hidden flex flex-col justify-start"
      style={{ background: "#FCFAEE" }}
      data-testid="measurement-page"
    >
      {/* Background glow layers */}
      <div 
        className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none filter blur-[120px] mix-blend-multiply opacity-70" 
        style={{ background: "rgba(42, 157, 143, 0.05)" }} 
      />
      <div 
        className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none filter blur-[100px] mix-blend-multiply opacity-70" 
        style={{ background: "rgba(0, 98, 143, 0.05)" }} 
      />

      <div className="relative z-10 w-full max-w-[760px] mx-auto flex-1">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={handleBack}
            className="w-10 h-10 rounded-2xl flex items-center justify-center transition-all hover:bg-white/90 active:scale-95 shadow-sm"
            style={{ background: "rgba(255, 255, 255, 0.7)", border: "1px solid rgba(226, 217, 203, 0.5)" }}
            data-testid="measurement-back-btn"
          >
            <ArrowLeft size={18} color="#71644C" />
          </button>
          <div>
            <div
              className="font-bold tracking-[1.5px] uppercase"
              style={{ fontSize: 10, color: "#71644C" }}
            >
              Wellness Hub
            </div>
            <div
              className="text-xl font-bold mt-0.5 text-[#2D2A24]"
              data-testid="measurement-title"
            >
              Nueva medición
            </div>
          </div>
        </div>

        {/* Warning banners */}
        <div className="mb-4 space-y-2">
          {isDemo && (
            <AlertBanner variant="warning" testId="measurement-demo-banner">
              Modo demo activo: las mediciones no se guardarán permanentemente.
            </AlertBanner>
          )}
          {isImpersonating && (
            <AlertBanner variant="warning" testId="measurement-impersonation-banner">
              Estás viendo el perfil de otro usuario: no puedes registrar mediciones en su nombre.
            </AlertBanner>
          )}
        </div>

        {/* PAR-Q Consent Gate */}
        {needsParqGate && (
          <div className="mb-4">
            <PARQConsentGate
              riskCount={parqRiskCount}
              onResolved={(type) => setParqConsentimiento(type)}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4">
            <AlertBanner variant="danger" testId="measurement-error">
              <AlertCircle size={14} className="inline mr-1.5 align-text-bottom" />
              {error}
            </AlertBanner>
          </div>
        )}

        {/* ── SELECTOR VIEW ─────────────────────────────────────── */}
        {tab === "selector" && (
          <div className="w-full mt-4 animate-fade-in" data-testid="measurement-selector-view">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#2D2A24] mb-2">Selecciona tu método de medición</h2>
              <p className="text-xs text-[#71644C] max-w-md mx-auto leading-relaxed">
                Registra tus signos vitales de forma rápida y segura. Elige la tecnología de escaneo facial rPPG o ingresa tus datos manualmente.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Opción Cámara */}
              <div 
                onClick={() => setTab("camera")}
                className="flex flex-col justify-between p-6 sm:p-8 bg-white/70 backdrop-blur-[20px] border border-white/40 rounded-3xl hover:bg-white/90 hover:shadow-[0_20px_50px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 group cursor-pointer shadow-[0_12px_40px_rgba(0,0,0,0.03)]"
                data-testid="measurement-select-camera"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 bg-[#EBFBFA] border border-[#2A9D8F]/20 shadow-inner">
                    <Camera size={28} className="text-[#2A9D8F]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#2D2A24] mb-3">Escaneo Facial rPPG</h3>
                  <p className="text-xs text-[#71644C] leading-relaxed mb-6 max-w-[260px]">
                    Mide tu frecuencia cardíaca, SpO2, estrés y más en ~50 segundos usando la cámara frontal de tu dispositivo.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTab("camera");
                  }}
                  className="w-full py-3.5 rounded-2xl text-xs font-bold text-white transition-all bg-gradient-to-r from-[#00628F] via-[#0080A0] to-[#2A9D8F] shadow-[0_6px_20px_rgba(42,157,143,0.15)] group-hover:shadow-[0_8px_25px_rgba(42,157,143,0.25)] hover:scale-[1.01] active:scale-[0.99]"
                >
                  Comenzar Escaneo
                </button>
              </div>

              {/* Opción Manual */}
              <div 
                onClick={() => setTab("manual")}
                className="flex flex-col justify-between p-6 sm:p-8 bg-white/70 backdrop-blur-[20px] border border-white/40 rounded-3xl hover:bg-white/90 hover:shadow-[0_20px_50px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 group cursor-pointer shadow-[0_12px_40px_rgba(0,0,0,0.03)]"
                data-testid="measurement-select-manual"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 bg-[#F0F7FF] border border-[#00628F]/20 shadow-inner">
                    <Edit3 size={28} className="text-[#00628F]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#2D2A24] mb-3">Registro Manual</h3>
                  <p className="text-xs text-[#71644C] leading-relaxed mb-6 max-w-[260px]">
                    Ingresa de forma manual tus parámetros clínicos y sube reportes clínicos para mantener un control detallado.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTab("manual");
                  }}
                  className="w-full py-3.5 rounded-2xl text-xs font-bold text-[#00628F] border border-[#00628F]/20 bg-white/50 hover:bg-[#00628F]/5 transition-all shadow-sm group-hover:border-[#00628F]/40 hover:scale-[1.01] active:scale-[0.99]"
                >
                  Ingresar Métricas
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── MANUAL VIEW ───────────────────────────────────────── */}
        {tab === "manual" && (
          <div className="w-full max-w-[560px] mx-auto mt-4 animate-fade-in" data-testid="measurement-manual-form">
            <div className="bg-white/70 backdrop-blur-[20px] border border-white/40 rounded-3xl p-6 sm:p-8 shadow-[0_12px_40px_rgba(0,0,0,0.05)]">
              <div className="mb-6">
                <h2 className="text-lg font-bold text-[#2D2A24]">Registro Manual Clínico</h2>
                <p className="text-[11px] text-[#71644C] mt-1 leading-relaxed">
                  Ingresa los valores que desees registrar hoy. Las demás métricas mantendrán su último registro histórico.
                </p>
              </div>

              {/* Symmetric Grid of Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Peso */}
                <div>
                  <TextInput
                    label={FIELD_META.peso.label}
                    value={values.peso || ""}
                    onChange={(v) => setField("peso", v)}
                    placeholder={FIELD_META.peso.placeholder}
                    suffix={FIELD_META.peso.unit}
                    type="number"
                    min={FIELD_META.peso.min}
                    max={FIELD_META.peso.max}
                    step={FIELD_META.peso.step}
                    testId="measurement-input-peso"
                  />
                </div>
                {/* Talla */}
                <div>
                  <TextInput
                    label={FIELD_META.talla.label}
                    value={values.talla || ""}
                    onChange={(v) => setField("talla", v)}
                    placeholder={FIELD_META.talla.placeholder}
                    suffix={FIELD_META.talla.unit}
                    type="number"
                    min={FIELD_META.talla.min}
                    max={FIELD_META.talla.max}
                    step={FIELD_META.talla.step}
                    testId="measurement-input-talla"
                  />
                </div>
                {/* Cintura */}
                <div>
                  <TextInput
                    label={FIELD_META.cintura.label}
                    value={values.cintura || ""}
                    onChange={(v) => setField("cintura", v)}
                    placeholder={FIELD_META.cintura.placeholder}
                    suffix={FIELD_META.cintura.unit}
                    type="number"
                    min={FIELD_META.cintura.min}
                    max={FIELD_META.cintura.max}
                    step={FIELD_META.cintura.step}
                    testId="measurement-input-cintura"
                  />
                </div>
                {/* Frecuencia Cardíaca */}
                <div>
                  <TextInput
                    label={FIELD_META.heartRate.label}
                    value={values.heartRate || ""}
                    onChange={(v) => setField("heartRate", v)}
                    placeholder={FIELD_META.heartRate.placeholder}
                    suffix={FIELD_META.heartRate.unit}
                    type="number"
                    min={FIELD_META.heartRate.min}
                    max={FIELD_META.heartRate.max}
                    step={FIELD_META.heartRate.step}
                    testId="measurement-input-heartRate"
                  />
                </div>

                {/* Presión Arterial */}
                <div className="sm:col-span-2 bg-[#FAF8F0]/80 p-4 rounded-2xl border border-white/60">
                  <p className="text-xs font-bold text-[#2D2A24] mb-0.5">
                    Presión arterial
                  </p>
                  <p className="text-[10px] text-[#71644C] mb-3">
                    Sistólica y diastólica se miden siempre en conjunto
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <TextInput
                      label={FIELD_META.paSistolica.label}
                      value={values.paSistolica || ""}
                      onChange={(v) => setField("paSistolica", v)}
                      placeholder={FIELD_META.paSistolica.placeholder}
                      suffix={FIELD_META.paSistolica.unit}
                      type="number"
                      min={FIELD_META.paSistolica.min}
                      max={FIELD_META.paSistolica.max}
                      step={FIELD_META.paSistolica.step}
                      testId="measurement-input-paSistolica"
                    />
                    <TextInput
                      label={FIELD_META.paDiastolica.label}
                      value={values.paDiastolica || ""}
                      onChange={(v) => setField("paDiastolica", v)}
                      placeholder={FIELD_META.paDiastolica.placeholder}
                      suffix={FIELD_META.paDiastolica.unit}
                      type="number"
                      min={FIELD_META.paDiastolica.min}
                      max={FIELD_META.paDiastolica.max}
                      step={FIELD_META.paDiastolica.step}
                      testId="measurement-input-paDiastolica"
                    />
                  </div>
                </div>

                {/* Oximetria */}
                <div>
                  <TextInput
                    label={FIELD_META.oximetria.label}
                    value={values.oximetria || ""}
                    onChange={(v) => setField("oximetria", v)}
                    placeholder={FIELD_META.oximetria.placeholder}
                    suffix={FIELD_META.oximetria.unit}
                    type="number"
                    min={FIELD_META.oximetria.min}
                    max={FIELD_META.oximetria.max}
                    step={FIELD_META.oximetria.step}
                    testId="measurement-input-oximetria"
                  />
                </div>
                {/* Glucemia */}
                <div>
                  <TextInput
                    label={FIELD_META.glucemia.label}
                    value={values.glucemia || ""}
                    onChange={(v) => setField("glucemia", v)}
                    placeholder={FIELD_META.glucemia.placeholder}
                    suffix={FIELD_META.glucemia.unit}
                    type="number"
                    min={FIELD_META.glucemia.min}
                    max={FIELD_META.glucemia.max}
                    step={FIELD_META.glucemia.step}
                    testId="measurement-input-glucemia"
                  />
                </div>
              </div>

              {/* Dropzone mockup/visual */}
              <div className="mt-5 mb-5">
                <label className="block text-xs font-bold text-[#2D2A24] mb-2">
                  Reporte clínico o ECG (opcional)
                </label>
                <div className="border border-dashed border-[#00628F]/30 bg-white/40 rounded-2xl p-5 text-center cursor-pointer hover:bg-white/60 hover:border-[#00628F]/50 transition-all duration-300">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-[#F0F7FF] flex items-center justify-center mb-2">
                      <UploadCloud size={18} className="text-[#00628F]" />
                    </div>
                    <span className="text-xs font-bold text-[#2D2A24]">Arrastra un archivo PDF o imagen</span>
                    <span className="text-[10px] text-[#71644C] mt-1">Soporta reportes de laboratorio o trazados de ECG (máx. 10MB)</span>
                  </div>
                </div>
              </div>

              {/* Nota */}
              <div className="mb-6">
                <label className="block text-xs font-bold text-[#2D2A24] mb-1.5">
                  Nota adicional (opcional)
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Ej: medición en ayunas, después de hacer ejercicio, etc."
                  rows={2}
                  className="w-full text-xs p-3.5 rounded-2xl border border-white/50 bg-white/40 placeholder-[#94A3B8] focus:outline-none focus:ring-1 focus:ring-[#00628F] transition-all"
                  data-testid="measurement-input-note"
                  style={{ minHeight: 60, resize: "vertical" }}
                />
              </div>

              {/* Submit Button */}
              <button
                type="button"
                onClick={handleSubmitManual}
                disabled={saving || filledCount === 0 || isReadOnly}
                className="w-full active:scale-[0.98] transition-all"
                style={{
                  padding: 14,
                  borderRadius: 16,
                  border: "none",
                  background:
                    filledCount === 0 || isReadOnly
                      ? "#E2D9CB"
                      : "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)",
                  color: filledCount === 0 || isReadOnly ? "#94A3B8" : "#FFFFFF",
                  fontSize: 13,
                  fontWeight: 700,
                  boxShadow:
                    filledCount === 0 || isReadOnly
                      ? "none"
                      : "0 6px 20px rgba(0,98,143,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  cursor:
                    saving || filledCount === 0 || isReadOnly
                      ? "not-allowed"
                      : "pointer",
                }}
                data-testid="measurement-submit-manual"
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Guardando…
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} /> Guardar medición
                    {filledCount > 0 && ` (${filledCount})`}
                  </>
                )}
              </button>

              {/* Back button to Selector */}
              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={() => setTab("selector")}
                  className="text-xs font-semibold text-[#00628F] hover:underline transition-all"
                >
                  &lt; Volver a opciones de medición
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── CAMERA VIEW ───────────────────────────────────────── */}
        {tab === "camera" && (
          <div className="w-full max-w-[520px] mx-auto mt-4 animate-fade-in" data-testid="measurement-camera-panel">
            <div className="bg-white/70 backdrop-blur-[20px] border border-white/40 rounded-3xl p-6 sm:p-8 shadow-[0_12px_40px_rgba(0,0,0,0.05)]">
              
              {/* Central circular scanner graphic / Hero */}
              <div className="flex flex-col items-center text-center mb-8 relative">
                <div className="relative w-44 h-44 rounded-full flex items-center justify-center p-1.5 bg-gradient-to-tr from-[#2A9D8F] via-[#5EEAD4] to-[#00628F] animate-pulse-slow shadow-lg">
                  {/* Inner dark matte circle to look like camera view finder */}
                  <div className="w-full h-full rounded-full bg-[#181D1A] overflow-hidden flex flex-col items-center justify-center relative border border-white/20">
                    {/* Soft grid lines or radar scanner effect */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(42,157,143,0.15)_0%,transparent_70%)]" />
                    <div className="absolute inset-0 border border-white/5 rounded-full scale-[0.8] border-dashed" />
                    <div className="absolute inset-0 border border-white/5 rounded-full scale-[0.6] border-dashed" />
                    
                    {/* Elegant camera icon with premium styling */}
                    <div className="z-10 flex flex-col items-center">
                      <Camera size={42} className="text-[#5EEAD4] drop-shadow-[0_0_10px_rgba(94,234,212,0.4)] mb-2" />
                      <span className="text-[9px] font-bold uppercase tracking-[1.5px] text-[#A7F3D0]">rPPG Ready</span>
                    </div>
                    
                    {/* Pulsing scanning ring inside */}
                    <div className="absolute inset-0 border-2 border-[#5EEAD4]/30 rounded-full animate-ping pointer-events-none" style={{ animationDuration: '3s' }} />
                  </div>
                </div>
                
                {/* Floating Telemetry Glass Bar below scanner */}
                <div className="mt-5 w-full bg-white/50 backdrop-blur-[10px] border border-white/50 rounded-2xl py-3 px-4 flex items-center justify-around shadow-sm max-w-[340px]">
                  <div className="flex flex-col items-center">
                    <span className="text-[10px] text-[#71644C] font-semibold">Frecuencia</span>
                    <span className="text-xs font-bold text-[#2D2A24] flex items-center gap-1 mt-0.5">
                      <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" />
                      -- <span className="text-[9px] font-semibold text-[#71644C]">lpm</span>
                    </span>
                  </div>
                  <div className="w-[1px] h-6 bg-white/60" />
                  <div className="flex flex-col items-center">
                    <span className="text-[10px] text-[#71644C] font-semibold">Saturación</span>
                    <span className="text-xs font-bold text-[#2D2A24] mt-0.5">
                      -- <span className="text-[9px] font-semibold text-[#71644C]">% SpO2</span>
                    </span>
                  </div>
                  <div className="w-[1px] h-6 bg-white/60" />
                  <div className="flex flex-col items-center">
                    <span className="text-[10px] text-[#71644C] font-semibold">Estrés</span>
                    <span className="text-xs font-bold text-[#2D2A24] mt-0.5">
                      --
                    </span>
                  </div>
                </div>
              </div>

              {/* Camera check and conditional options */}
              {!canUseCamera ? (
                <div className="mb-6 animate-fade-in">
                  <AlertBanner
                    variant="info"
                    testId="measurement-camera-unavailable"
                  >
                    La cámara está disponible solo en producción (we360.saluddigital360.com).
                    Por favor, utiliza la pestaña de Registro Manual en entornos locales.
                  </AlertBanner>
                </div>
              ) : (
                <>
                  {/* Instructions glass card */}
                  <div className="mb-6 bg-[#EBFBFA]/70 border border-[#2A9D8F]/20 rounded-2xl p-4 animate-fade-in">
                    <h4 className="text-xs font-bold text-[#0F766E] mb-2.5">
                      Recomendaciones para un escaneo clínico exitoso:
                    </h4>
                    <div className="space-y-2">
                      {[
                        { icon: "☀️", text: "Asegura un ambiente con iluminación frontal óptima." },
                        { icon: "🧍", text: "Mantén el rostro centrado y sin gesticular ni moverte." },
                        { icon: "👓", text: "Evita el uso de lentes reflectantes o maquillaje pesado." },
                      ].map((cond, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <span className="text-sm leading-none">{cond.icon}</span>
                          <span className="text-[#0F4C4C] font-semibold leading-relaxed">
                            {cond.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Trigger Scan Button */}
                  <button
                    type="button"
                    onClick={openCamera}
                    disabled={saving}
                    className="w-full active:scale-[0.98] transition-all duration-300"
                    style={{
                      padding: 15,
                      borderRadius: 16,
                      border: "none",
                      background: "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)",
                      color: "#FFFFFF",
                      fontSize: 13,
                      fontWeight: 700,
                      boxShadow: "0 6px 20px rgba(0,98,143,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      cursor: saving ? "wait" : "pointer",
                    }}
                    data-testid="measurement-open-camera"
                  >
                    {saving ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Guardando…
                      </>
                    ) : (
                      <>
                        <Camera size={16} /> Iniciar escaneo facial
                      </>
                    )}
                  </button>
                </>
              )}

              {/* Back button to Selector */}
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setTab("selector")}
                  className="text-xs font-semibold text-[#00628F] hover:underline transition-all"
                >
                  &lt; Volver a opciones de medición
                </button>
              </div>

            </div>
          </div>
        )}

        {/* Camera modal */}
        {showVitalsModal && (
          <VitalsErrorBoundary onError={() => setShowVitalsModal(false)}>
            <VitalsMeasurement
              isOpen={showVitalsModal}
              onClose={() => setShowVitalsModal(false)}
              onComplete={handleCameraComplete}
              userInfo={{
                age: userAge,
                sex: userSex,
                height: parseFloat(profile.talla),
                weight: parseFloat(profile.peso),
                bp_group: "normal",
              }}
            />
          </VitalsErrorBoundary>
        )}
      </div>
    </div>
  );
}
